import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

type Variant = 'primary' | 'secondary' | 'inverse';
type Size = 'md' | 'lg';

interface CommonProps {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
}

interface LinkProps extends CommonProps {
  to: string;
  href?: never;
}
interface AnchorProps extends CommonProps {
  href: string;
  to?: never;
}

type Props = LinkProps | AnchorProps;

const base =
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold no-underline transition-colors duration-150 select-none';

const sizes: Record<Size, string> = {
  md: 'px-5 py-2.5 text-[0.95rem]',
  lg: 'px-7 py-3.5 text-base',
};

const variants: Record<Variant, string> = {
  primary:
    'bg-[var(--color-sage-deep)] text-[var(--color-cream)] hover:bg-[var(--color-ink)] shadow-[var(--shadow-cta)]',
  secondary:
    'bg-transparent text-[var(--color-sage-deep)] border border-[var(--color-sage-deep)] hover:bg-[var(--color-sage-deep)] hover:text-[var(--color-cream)]',
  inverse:
    'bg-[var(--color-tan)] text-[var(--color-ink)] hover:bg-[var(--color-cream)] shadow-[var(--shadow-cta)]',
};

export function CTAButton(props: Props) {
  const { children, variant = 'primary', size = 'md', className = '' } = props;
  const classes = `${base} ${sizes[size]} ${variants[variant]} ${className}`;

  if ('to' in props && props.to) {
    return (
      <Link to={props.to} className={classes}>
        {children}
        <span aria-hidden>→</span>
      </Link>
    );
  }
  return (
    <a href={props.href} className={classes}>
      {children}
      <span aria-hidden>→</span>
    </a>
  );
}
